import { useAuthState } from '@/app/providers/auth'
import { useLocale } from '@/components/Link'
import {
  buildCateringTeamOrderLoginHref,
  getBasketIdParam,
  isCateringTeamOrderShareVisit,
} from '@/components/page-specific/catering/common/helpers/basket-link'
import { useGetBasketQuery } from '@/generated/graphql'
import { useCateringTeamOrderEnabled } from '@/helpers/features/cateringTeamOrder'
import { useNotificationStore } from '@/store/notificationStore'

import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useRef } from 'react'

import { classifyBasketFetch } from './menu-page/get-basket-query'

export type CateringTeamOrderEntry =
  | { status: 'none' }
  | { status: 'awaiting-auth' }
  | { status: 'redirecting' }
  | { status: 'resolving' }
  | { status: 'unavailable' }
  | { status: 'resolved' }

export const isCateringTeamOrderEntrySettled = (entry: CateringTeamOrderEntry) =>
  entry.status === 'none' || entry.status === 'resolved' || entry.status === 'unavailable'

/**
 * Resolves a team-order share-link visit at the page-shell level, before the streamed
 * sidebar/menu/cart chunk mounts. Owning it here rather than in useShoppingCart matters twice:
 * the unauthenticated redirect is a hard navigation fired before the sidebar can rewrite the URL
 * (a history.replaceState makes Next discard an in-flight router.replace), and the basket fetch
 * runs in parallel with the menu stream so the cart lands in one paint.
 */
export const useCateringTeamOrderEntry = (): CateringTeamOrderEntry => {
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const locale = useLocale()
  const authState = useAuthState()
  const shareVisit = isCateringTeamOrderShareVisit(searchParams)
  // Logged-out invitees have no accountId yet; this lets an explicit share-visit rule admit them
  // before the account rule can apply post-login (PT-2267).
  const cateringTeamOrderEnabled = useCateringTeamOrderEnabled({
    groupOrderShareVisit: shareVisit,
  })

  const basketId = getBasketIdParam(searchParams)
  const isShareVisit = cateringTeamOrderEnabled && shareVisit && Boolean(basketId)
  const isUnauthenticatedVisitor = isShareVisit && authState.type === 'unauthenticated'

  // Same GetBasketDocument the cart's lazy queries use, so they resolve from cache once mounted.
  // Logged-out visitors fetch too, so a dead link is reported here instead of bouncing them
  // through login/signup to find out (PT-1709).
  const result = useGetBasketQuery({
    variables: { id: basketId },
    skip: !isShareVisit || authState.type === 'loading',
  })
  const basket = result.data?.basket ?? null
  const outcome = classifyBasketFetch(result)
  const settled = outcome !== 'idle' && outcome !== 'loading'
  // Anything but a definite miss (UNAUTHENTICATED, a readable basket, a codeless network
  // failure) keeps the legacy redirect, so nobody is stranded on a false "no longer exists".
  const basketIsGone = isUnauthenticatedVisitor && outcome === 'gone'

  const addNotification = useNotificationStore((state) => state.addNotification)
  const handledRef = useRef(false)
  useEffect(() => {
    if (!isUnauthenticatedVisitor || !settled || handledRef.current) return
    handledRef.current = true
    if (basketIsGone) {
      addNotification({
        severity: 'error',
        message: 'This basket no longer exists. It may have been placed, deleted, or expired.',
      })
      return
    }
    window.location.replace(
      buildCateringTeamOrderLoginHref({
        pathname: pathname ?? '',
        searchParams,
        locale: locale ?? undefined,
      }),
    )
  }, [
    isUnauthenticatedVisitor,
    settled,
    basketIsGone,
    addNotification,
    pathname,
    searchParams,
    locale,
  ])

  if (!isShareVisit) return { status: 'none' }
  if (authState.type === 'loading') return { status: 'awaiting-auth' }
  if (authState.type === 'unauthenticated') {
    if (basketIsGone) return { status: 'unavailable' }
    return settled ? { status: 'redirecting' } : { status: 'resolving' }
  }
  if (outcome === 'loading') return { status: 'resolving' }
  return { status: 'resolved' }
}
